import { EntitiesExtension } from "@circle-react-shared/uikit/TipTap/Extensions/CustomExtensions/Entities";
import { MentionsExtension } from "@circle-react-shared/uikit/TipTap/Extensions/CustomExtensions/Mentions";
import { useInternalEditorState } from "@circle-react-shared/uikit/TipTap/internalEditorState";
import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { UndoRedo as History, Placeholder } from "@tiptap/extensions";
import { ReactNodeViewRenderer, useEditor } from "@tiptap/react";
import { useEffect, useRef, useState } from "react";

import { CircleAiEntityDisplay } from "./Entities/CircleAiEntityDisplay";
import { CircleAiMentionDisplay } from "./Mentions/CircleAiMentionDisplay";
import { hasMeaningfulContent } from "./tiptapToAnnotatedText";

interface UseCircleAiEditorOptions {
  onSubmit: () => void;
  placeholder: string;
}

const EDITOR_CLASS =
  "text-primary placeholder:text-tertiary w-full resize-none border-none bg-transparent p-0 align-middle text-sm leading-5 placeholder:opacity-100 focus:outline-none focus:ring-0";

export const useCircleAiEditor = ({
  placeholder,
  onSubmit,
}: UseCircleAiEditorOptions) => {
  const [hasContent, setHasContent] = useState(false);
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  // The placeholder can change after the editor exists (e.g. a plan is proposed).
  // Read the current value through a ref in Placeholder's function form,
  // then refresh the decoration when it changes (see effect below).
  const placeholderRef = useRef(placeholder);
  placeholderRef.current = placeholder;

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      HardBreak,
      History,
      Placeholder.configure({ placeholder: () => placeholderRef.current }),
      MentionsExtension.extend({
        addNodeView: () => ReactNodeViewRenderer(CircleAiMentionDisplay),
      }),
      EntitiesExtension.extend({
        addNodeView: () => ReactNodeViewRenderer(CircleAiEntityDisplay),
      }),
    ],
    onUpdate: ({ editor }) => {
      setHasContent(hasMeaningfulContent(editor.getJSON()));
    },
    editorProps: {
      attributes: { class: EDITOR_CLASS },
      handleKeyDown: (_view: any, event: KeyboardEvent) => {
        if (event.isComposing || event.keyCode === 229) return false;
        if (event.key !== "Enter" || event.shiftKey) return false;

        if (useInternalEditorState.getState().arePopupsVisible) return false;

        event.preventDefault();
        onSubmitRef.current();
        return true;
      },
    },
  });

  // Placeholder decorations only recompute on a transaction, and a placeholder
  // change usually has none — dispatch an empty transaction (public API, no doc/
  // selection change, doesn't fire onUpdate) so the new text renders immediately.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.view.dispatch(editor.state.tr);
  }, [editor, placeholder]);

  return { editor, hasContent };
};
